import axios from 'axios';
import { User } from 'oidc-client';
import { Dispatch, useCallback, useReducer } from 'react';
import { Log } from '@utils/logger';
import AuthState, { defaultAuthState } from './AuthState';

type Action =
  | { type: 'LOADING' }
  | { type: 'INITIALISED'; user: User | undefined }
  | { type: 'LOGOUT' }
  | { type: 'ERROR'; error: Error };

export const reducer = (state: AuthState, action: Action): AuthState => {
  Log.debug('Auth Reducer', action.type, state, action);

  switch (action.type) {
    case 'INITIALISED':
      if (!!action.user) {
        axios.defaults.headers.Authorization = 'Bearer ' + action.user.access_token;
      }
      return {
        ...state,
        isAuthenticated: !!action.user,
        user: action.user,
        isLoading: false,
        error: undefined
      };
    case 'LOADING':
      return {
        ...state,
        isLoading: true
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: undefined
      };
    case 'ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.error
      };
    default:
      return state;
  }
};

const onLoading = (dispatch: Dispatch<Action>) => () => dispatch({ type: 'LOADING' });
const loadUser = (dispatch: Dispatch<Action>) => (user?: User) => dispatch({ type: 'INITIALISED', user });
const onLogout = (dispatch: Dispatch<Action>) => () => dispatch({ type: 'LOGOUT' });
const onError = (dispatch: Dispatch<Action>) => (error: Error) => dispatch({ type: 'ERROR', error });

export interface UseAuthenticationContextStateType {
  onError: Function;
  loadUser: Function;
  onLoading: Function;
  onLogout: Function;
  authState: AuthState;
}

export const useAuthenticationContextState = (): UseAuthenticationContextStateType => {
  const [authState, dispatch] = useReducer(reducer, defaultAuthState);

  return {
    onError: useCallback((error) => onError(dispatch)(error), []),
    loadUser: useCallback((user) => loadUser(dispatch)(user), []),
    onLoading: useCallback(() => onLoading(dispatch)(), []),
    onLogout: useCallback(() => onLogout(dispatch)(), []),
    authState
  };
};
